import { Copy, Users, Mail, Phone, FileText, Calendar, Hash, History, ArrowRight, Package, X, Plus, Trash2, Star } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import toast from 'react-hot-toast';
import {
    Dialog,
    DialogPortal,
    DialogOverlay,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogClose,
} from "../ui/dialog";

interface Customer {
    id: string;
    name: string;
    email: string;
    phone?: string;
    code?: string;
    status: string;
    createdAt: string;
}

interface CustomerDrawerProps {
    customer: Customer | null;
    open: boolean;
    onClose: () => void;
}

const CONTACT_ROLES = [
    { value: '', label: 'Role (optional)' },
    { value: 'shipping', label: 'Shipping' },
    { value: 'logistics', label: 'Logistics' },
    { value: 'purchasing', label: 'Purchasing' },
    { value: 'accounting', label: 'Accounting' },
    { value: 'general', label: 'General' },
];

const NOTIFY_FLAGS = [
    { key: 'notifyPod',         label: 'POD' },
    { key: 'notifyShortage',    label: 'Shortage' },
    { key: 'notifyAppointment', label: 'Appointment' },
    { key: 'notifyDelay',       label: 'Delay' },
    { key: 'notifyFollowupPo',  label: 'Follow-up PO' },
] as const;

interface Contact {
    id: string;
    email: string;
    name: string | null;
    role: string | null;
    isPrimary: boolean;
    receivesNotifications: boolean;
    notifyPod: boolean;
    notifyShortage: boolean;
    notifyAppointment: boolean;
    notifyDelay: boolean;
    notifyFollowupPo: boolean;
}

export function CustomerDrawer({ customer, open, onClose }: CustomerDrawerProps) {
    const [, setLocation] = useLocation();
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [addingContact, setAddingContact] = useState(false);
    const [expandedFlags, setExpandedFlags] = useState<Set<string>>(new Set());
    const [newEmail, setNewEmail] = useState('');
    const [newName, setNewName] = useState('');
    const [newRole, setNewRole] = useState('');
    const [newReceives, setNewReceives] = useState(true);
    const [savingContact, setSavingContact] = useState(false);

    // Fetch contacts whenever drawer opens for a customer
    useEffect(() => {
        if (!open || !customer) { setContacts([]); return; }
        const storage = localStorage.getItem('auth-storage');
        const token = storage ? JSON.parse(storage).state.token : null;
        if (!token) return;
        fetch(`/api/customers/${customer.id}/contacts`, {
            headers: { Authorization: `Bearer ${token}` }
        })
            .then(r => r.ok ? r.json() : Promise.resolve({ contacts: [] }))
            .then(d => setContacts(d.contacts ?? []))
            .catch(() => setContacts([]));
    }, [open, customer]);

    const handleAddContact = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newEmail.trim() || !customer) return;
        const storage = localStorage.getItem('auth-storage');
        const token = storage ? JSON.parse(storage).state.token : null;
        if (!token) return;
        setSavingContact(true);
        try {
            const res = await fetch(`/api/customers/${customer.id}/contacts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                    email: newEmail.trim(),
                    name: newName.trim() || null,
                    role: newRole || null,
                    isPrimary: contacts.length === 0,
                    receivesNotifications: newReceives,
                    // Mirror master toggle to all granular flags on creation
                    notifyPod: newReceives,
                    notifyShortage: newReceives,
                    notifyAppointment: newReceives,
                    notifyDelay: newReceives,
                    notifyFollowupPo: newReceives,
                })
            });
            if (!res.ok) {
                const err = await res.json();
                toast.error(err.error || 'Failed to add contact');
                return;
            }
            const { contact } = await res.json();
            setContacts(prev => [...prev, contact]);
            setNewEmail(''); setNewName(''); setNewRole(''); setNewReceives(true);
            setAddingContact(false);
            toast.success('Contact added');
        } catch { toast.error('Network error'); }
        finally { setSavingContact(false); }
    };

    const patchContact = async (contactId: string, patch: Record<string, any>) => {
        if (!customer) return;
        const storage = localStorage.getItem('auth-storage');
        const token = storage ? JSON.parse(storage).state.token : null;
        if (!token) return;
        // Optimistic update
        setContacts(prev => prev.map(c => c.id === contactId ? { ...c, ...patch } : c));
        try {
            const res = await fetch(`/api/customers/${customer.id}/contacts/${contactId}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify(patch)
            });
            if (!res.ok) {
                const err = await res.json();
                toast.error(err.error || 'Failed to save');
                // Roll back optimistic update
                const r = await fetch(`/api/customers/${customer.id}/contacts`, { headers: { Authorization: `Bearer ${token}` } });
                if (r.ok) { const d = await r.json(); setContacts(d.contacts ?? []); }
            }
        } catch { toast.error('Network error'); }
    };

    const toggleFlags = (contactId: string) => {
        setExpandedFlags(prev => {
            const next = new Set(prev);
            if (next.has(contactId)) next.delete(contactId); else next.add(contactId);
            return next;
        });
    };

    const handleDeleteContact = async (contactId: string) => {
        const storage = localStorage.getItem('auth-storage');
        const token = storage ? JSON.parse(storage).state.token : null;
        if (!token || !customer) return;
        try {
            const res = await fetch(`/api/customers/${customer.id}/contacts/${contactId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) { toast.error('Failed to delete contact'); return; }
            setContacts(prev => prev.filter(c => c.id !== contactId));
            toast.success('Contact removed');
        } catch { toast.error('Network error'); }
    };

    const copyToClipboard = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success(`${label} copied to clipboard`);
        } catch (err) {
            toast.error(`Failed to copy ${label}`);
            console.error('Copy failed:', err);
        }
    };

    if (!customer) return null;

    return (
        <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
            <DialogPortal>
                <DialogOverlay />
                <DialogContent className="w-[90vw] max-w-2xl flex flex-col max-h-[90vh]">
                    <DialogHeader className="p-6 border-b border-border sticky top-0 bg-card/95 backdrop-blur z-10 text-left">
                        <div className="flex items-center justify-between">
                            <DialogTitle className="text-2xl font-bold font-outfit flex items-center gap-2">
                                <Users className="text-primary w-6 h-6" />
                                {customer.name}
                            </DialogTitle>
                            <DialogClose className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
                                <X size={20} />
                            </DialogClose>
                        </div>
                        <DialogDescription>
                            Customer Account Details
                        </DialogDescription>
                        <div className="mt-2 flex items-center gap-2">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${customer.status === 'active'
                                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                                : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                                }`}>
                                {customer.status.toUpperCase()}
                            </span>
                            {customer.code && (
                                <span className="font-mono text-xs bg-primary/10 px-2 py-0.5 rounded text-primary border border-primary/20">
                                    {customer.code}
                                </span>
                            )}
                        </div>
                    </DialogHeader>

                    <div className="p-6 space-y-8 overflow-y-auto flex-1">

                        {/* Account Info */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Account Details</h3>
                            <div className="bg-muted/30 p-4 rounded-xl border border-border">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-primary/10 text-primary rounded-lg">
                                            <Hash size={16} />
                                        </div>
                                        <div>
                                            <div className="text-xs text-muted-foreground">Customer Code</div>
                                            <div className="font-medium font-mono">{customer.code || 'N/A'}</div>
                                        </div>
                                    </div>
                                    {customer.code && (
                                        <button onClick={() => copyToClipboard(customer.code!, 'Customer Code')} className="text-primary hover:text-primary/80 transition-colors">
                                            <Copy size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Contact Info */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Contact Information</h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between group p-2 -mx-2 rounded hover:bg-muted/50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-primary/10 text-primary rounded-lg">
                                            <Mail size={16} />
                                        </div>
                                        <div>
                                            <div className="text-xs text-muted-foreground">Email Address</div>
                                            <div className="font-medium break-all">{customer.email}</div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => copyToClipboard(customer.email, 'Email')}
                                        className="opacity-0 group-hover:opacity-100 p-2 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-all"
                                    >
                                        <Copy size={16} />
                                    </button>
                                </div>

                                {customer.phone && (
                                    <div className="flex items-center justify-between group p-2 -mx-2 rounded hover:bg-muted/50 transition-colors">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-primary/10 text-primary rounded-lg">
                                                <Phone size={16} />
                                            </div>
                                            <div>
                                                <div className="text-xs text-muted-foreground">Phone Number</div>
                                                <div className="font-medium">{customer.phone}</div>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(customer.phone!, 'Phone')}
                                            className="opacity-0 group-hover:opacity-100 p-2 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-all"
                                        >
                                            <Copy size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Contacts — multi-email */}
                        <div className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Contacts</h3>
                                <button
                                    onClick={() => setAddingContact(v => !v)}
                                    className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                                >
                                    <Plus size={12} /> Add
                                </button>
                            </div>

                            {contacts.length === 0 && !addingContact && (
                                <p className="text-xs text-muted-foreground italic px-1">
                                    No contacts yet — click Add to create one.
                                </p>
                            )}

                            <div className="space-y-2">
                                {contacts.map(c => (
                                    <div key={c.id} className="rounded-lg bg-muted/30 border border-border overflow-hidden">
                                        {/* Contact header row */}
                                        <div className="flex items-start gap-2 p-2.5">
                                            <div className="p-1.5 bg-primary/10 text-primary rounded shrink-0 mt-0.5">
                                                <Mail size={12} />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                    <span className="text-sm font-medium break-all">{c.email}</span>
                                                    {c.isPrimary && (
                                                        <span className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide bg-primary/10 text-primary px-1.5 py-0.5 rounded border border-primary/20">
                                                            <Star size={8} /> Primary
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                                    {c.name && <span className="text-xs text-muted-foreground">{c.name}</span>}
                                                    {c.role && (
                                                        <span className="text-[10px] font-medium bg-muted border border-border px-1.5 py-0.5 rounded capitalize">{c.role === 'receiving' ? 'shipping' : c.role}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                                <button
                                                    onClick={() => copyToClipboard(c.email, 'Email')}
                                                    className="p-1.5 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors"
                                                    title="Copy email"
                                                >
                                                    <Copy size={13} />
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteContact(c.id)}
                                                    className="p-1.5 hover:bg-red-500/10 rounded text-muted-foreground hover:text-red-400 transition-colors"
                                                    title="Remove contact"
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </div>

                                        {/* Notification controls */}
                                        <div className="border-t border-border/60 px-2.5 py-2 space-y-2">
                                            {/* Master toggle */}
                                            <div className="flex items-center justify-between">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={c.receivesNotifications}
                                                        onChange={e => patchContact(c.id, { receivesNotifications: e.target.checked })}
                                                        className="h-3.5 w-3.5 rounded border-input accent-primary"
                                                    />
                                                    <span className="text-xs text-muted-foreground">Receives notifications</span>
                                                </label>
                                                {c.receivesNotifications && (
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleFlags(c.id)}
                                                        className="text-[10px] text-primary hover:text-primary/80 font-medium transition-colors"
                                                    >
                                                        {expandedFlags.has(c.id) ? 'Hide flags ▲' : 'Manage flags ▼'}
                                                    </button>
                                                )}
                                            </div>

                                            {/* Granular flags — shown when expanded and receives_notifications=true */}
                                            {c.receivesNotifications && expandedFlags.has(c.id) && (
                                                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 pl-1">
                                                    {NOTIFY_FLAGS.map(flag => (
                                                        <label key={flag.key} className="flex items-center gap-1.5 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                checked={c[flag.key]}
                                                                onChange={e => patchContact(c.id, { [flag.key]: e.target.checked })}
                                                                className="h-3 w-3 rounded border-input accent-primary"
                                                            />
                                                            <span className="text-[11px] text-muted-foreground">{flag.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {addingContact && (
                                <form onSubmit={handleAddContact} className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2">
                                    <input
                                        type="email"
                                        value={newEmail}
                                        onChange={e => setNewEmail(e.target.value)}
                                        placeholder="Email address *"
                                        required
                                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <input
                                            type="text"
                                            value={newName}
                                            onChange={e => setNewName(e.target.value)}
                                            placeholder="Name (optional)"
                                            className="flex h-8 w-full rounded-md border border-input bg-background px-3 py-1.5 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                                        />
                                        <select
                                            value={newRole}
                                            onChange={e => setNewRole(e.target.value)}
                                            className="flex h-8 w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
                                        >
                                            {CONTACT_ROLES.map(r => (
                                                <option key={r.value} value={r.value}>{r.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={newReceives}
                                            onChange={e => setNewReceives(e.target.checked)}
                                            className="h-3.5 w-3.5 rounded border-input accent-primary"
                                        />
                                        <span className="text-[11px] text-muted-foreground">Receives notifications</span>
                                    </label>
                                    <div className="flex gap-2 justify-end">
                                        <button
                                            type="button"
                                            onClick={() => { setAddingContact(false); setNewEmail(''); setNewName(''); setNewRole(''); setNewReceives(true); }}
                                            className="h-8 px-3 text-xs font-medium rounded-md border border-input bg-background hover:bg-muted transition-colors"
                                        >Cancel</button>
                                        <button
                                            type="submit"
                                            disabled={savingContact || !newEmail.trim()}
                                            className="h-8 px-3 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
                                        >{savingContact ? 'Saving…' : 'Save Contact'}</button>
                                    </div>
                                </form>
                            )}
                        </div>

                        {/* History */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Activity</h3>
                            <div className="bg-muted/30 p-4 rounded-xl border border-border space-y-2">
                                <button
                                    onClick={() => {
                                        onClose();
                                        setLocation(`/customers/${customer.id}/orders`);
                                    }}
                                    className="w-full flex items-center justify-between group p-2 -mx-2 rounded hover:bg-muted/50 transition-colors text-left"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-primary/10 text-primary rounded-lg">
                                            <Package size={16} />
                                        </div>
                                        <div>
                                            <div className="font-medium">View Orders</div>
                                            <div className="text-xs text-muted-foreground">Manage purchase orders and shipments</div>
                                        </div>
                                    </div>
                                    <ArrowRight size={16} className="text-muted-foreground group-hover:text-foreground transition-colors" />
                                </button>

                                <div className="h-px bg-border/50 mx-2" />

                                <button
                                    onClick={() => {
                                        onClose();
                                        setLocation(`/customers/${customer.id}/history`);
                                    }}
                                    className="w-full flex items-center justify-between group p-2 -mx-2 rounded hover:bg-muted/50 transition-colors text-left"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-primary/10 text-primary rounded-lg">
                                            <History size={16} />
                                        </div>
                                        <div>
                                            <div className="font-medium">View History</div>
                                            <div className="text-xs text-muted-foreground">Recent purchase orders and activity</div>
                                        </div>
                                    </div>
                                    <ArrowRight size={16} className="text-muted-foreground group-hover:text-foreground transition-colors" />
                                </button>
                            </div>
                        </div>

                        {/* Aliases (Managers Only) - Minimal Entrypoint */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">Aliases (Exact Match)</h3>
                            <div className="bg-muted/30 p-4 rounded-xl border border-border">
                                <p className="text-xs text-muted-foreground mb-3">
                                    Add alternate names to auto-link POs. Case-insensitive, exact match only.
                                </p>
                                <form
                                    onSubmit={async (e) => {
                                        e.preventDefault();
                                        const input = e.currentTarget.elements.namedItem('alias') as HTMLInputElement;
                                        const value = input.value.trim();
                                        if (!value) return;

                                        const btn = e.currentTarget.querySelector('button');
                                        if (btn) btn.disabled = true;

                                        try {
                                            const storage = localStorage.getItem('auth-storage');
                                            const token = storage ? JSON.parse(storage).state.token : null;

                                            if (!token) { toast.error("Authentication error"); return; }

                                            const res = await fetch(`/api/customers/${customer.id}/aliases`, {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                                body: JSON.stringify({ alias: value })
                                            });

                                            if (!res.ok) {
                                                const err = await res.json();
                                                toast.error(err.message || err.error || 'Failed to add alias');
                                            } else {
                                                toast.success('Alias added. POs will auto-link on next scan.');
                                                input.value = '';
                                            }
                                        } catch (err) {
                                            console.error(err);
                                            toast.error('Network error');
                                        } finally {
                                            if (btn) btn.disabled = false;
                                        }
                                    }}
                                    className="flex gap-2"
                                >
                                    <div className="relative flex-1">
                                        <Hash className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground w-3.5 h-3.5" />
                                        <input
                                            name="alias"
                                            placeholder="Enter alias name..."
                                            className="w-full pl-8 pr-3 py-2 h-9 text-sm rounded-md border border-input bg-background focus:ring-1 focus:ring-primary"
                                            autoComplete="off"
                                        />
                                    </div>
                                    <button type="submit" className="h-9 px-3 bg-primary text-primary-foreground text-xs font-medium rounded-md hover:bg-primary/90">
                                        Add
                                    </button>
                                </form>
                            </div>
                        </div>

                        {/* System Info */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-bold text-muted-foreground uppercase tracking-wider">System Metadata</h3>
                            <div className="bg-muted/30 p-4 rounded-xl border border-border space-y-3">
                                <div className="flex items-center gap-2 text-sm">
                                    <Calendar size={14} className="text-muted-foreground" />
                                    <span className="text-muted-foreground">Member Since:</span>
                                    <span className="font-mono ml-auto">{new Date(customer.createdAt).toLocaleDateString()}</span>
                                </div>
                                <div className="flex items-center gap-2 text-sm">
                                    <FileText size={14} className="text-muted-foreground" />
                                    <span className="text-muted-foreground">System ID:</span>
                                    <code className="text-xs bg-muted px-1 py-0.5 rounded ml-auto">{customer.id}</code>
                                </div>
                            </div>
                        </div>

                    </div>
                </DialogContent>
            </DialogPortal>
        </Dialog>
    );
}
